import { createElement, useCallback, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import SerpContentSnapshot from '@/components/Approval/@components/SerpContentSnapshot';
import type { ElementTransform } from '@/components/Common/HtmlToReact';
import { formatSerpText } from '@/utils/approval/serpDocument';
import type {
  SerpSellingPriceItem,
  SerpSellingPriceSource,
} from '@/utils/approval/serpSellingPrice';
import { SerpSellingPriceRoot } from './styles';

/** 레거시 문서와 같은 id — 버튼 data-value 가 접고 펼 영역 id */
const COLOR_DETAIL_ID = 'detail_view_dtl';
const STYLE_DETAIL_ID = 'detail_view_disc';
const DETAIL_IDS = new Set([COLOR_DETAIL_ID, STYLE_DETAIL_ID]);

type TableProps = {
  source: SerpSellingPriceSource;
  t: TFunction;
};

const DetailHeader = ({
  title,
  detailId,
  t,
}: {
  title: string;
  detailId: string;
  t: TFunction;
}) => (
  <table className="table table-bordered-horizontal mb-0 text-center">
    <colgroup>
      <col width="*" />
      <col width="90px" />
    </colgroup>
    <thead>
      <tr>
        <th className="bg-light">{title}</th>
        <th className="bg-light">
          <button type="button" data-value={detailId}>
            {t('상세 보기')}
          </button>
        </th>
      </tr>
    </thead>
  </table>
);

const ItemRow = ({
  item,
  withColor,
}: {
  item: SerpSellingPriceItem;
  withColor: boolean;
}) => (
  <tr>
    <td>{formatSerpText(item.SSN_CD)}</td>
    <td>{formatSerpText(item.PROD_CD)}</td>
    {withColor && <td>{formatSerpText(item.COLOR_CD)}</td>}
    <td colSpan={3} className="text-left">
      {formatSerpText(item.PROD_KO_NM)}
    </td>
    <td className="text-right">{formatSerpText(item.DISC_PRC)}</td>
    <td className="text-right">{formatSerpText(item.SALE_URC)}</td>
    <td>{formatSerpText(item.DISC_RAT)}</td>
  </tr>
);

const ItemHead = ({ withColor, t }: { withColor: boolean; t: TFunction }) => (
  <thead>
    <tr>
      <td>{t('시즌')}</td>
      <td>{t('스타일')}</td>
      {withColor && <td>{t('컬러')}</td>}
      <td colSpan={3}>{t('설명')}</td>
      <td>{t('적용단가')}</td>
      <td>{t('정상가')}</td>
      <td>{t('할인율')}</td>
    </tr>
  </thead>
);

/** 레거시 #APV_SERP_SALES .form-group 의 표(금액·할인율은 응답 글자 그대로). 상세 표는 접힌 채로 저장 */
const SerpSellingPriceTables = ({ source, t }: TableProps) => {
  const period = {
    start: formatSerpText(source.APLY_STA_DE),
    end: formatSerpText(source.APLY_END_DE),
  };
  const shops = source.SHOP_LIST ?? [];
  const colors = source.COLOR_INFO?.COLOR_LIST ?? [];
  const styles = source.PROD_INFO?.PROD_LIST ?? [];
  return (
    <>
      <table className="table table-bordered-horizontal mb-30 text-center">
        <tbody>
          <tr>
            <th colSpan={10} className="bg-light">
              {`${formatSerpText(source.BRAND_NM)} ${formatSerpText(
                source.APRV_TIT,
              )}`}
            </th>
          </tr>
          <tr>
            <th colSpan={10} className="text-left">
              {t(
                '적용대상 매장 리스트(적용기간 : {{start}} ~ {{end}})',
                period,
              )}
            </th>
          </tr>
          {source.ALL_SHOP_YN === 'Y' ? (
            <tr>
              <td colSpan={10} className="text-left">
                {t('전체매장 적용')}
              </td>
            </tr>
          ) : (
            <>
              <tr>
                <td colSpan={5}>{t('매장코드')}</td>
                <td colSpan={5}>{t('매장명')}</td>
              </tr>
              {shops.map((shop, index) => (
                <tr key={index}>
                  <td colSpan={5}>{formatSerpText(shop.SHOP_CD)}</td>
                  <td colSpan={5}>{formatSerpText(shop.SHOP_ABBR_NM)}</td>
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>
      <div className="mb-20">
        <DetailHeader
          title={t(
            '적용 대상 스타일-컬러 리스트(적용기간 : {{start}} ~ {{end}})',
            period,
          )}
          detailId={COLOR_DETAIL_ID}
          t={t}
        />
        <div
          id={COLOR_DETAIL_ID}
          className="table-area"
          data-state="closed"
          style={{ display: 'none' }}
        >
          <table className="table table-bordered-horizontal mb-30 text-center sales-table">
            <ItemHead withColor t={t} />
            <tbody>
              {colors.map((item, index) => (
                <ItemRow key={index} item={item} withColor />
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={10} className="text-right">
                  {t('총 {{total}} 스타일-컬러', {
                    total: formatSerpText(source.COLOR_INFO?.COLOR_CNT),
                  })}
                </td>
              </tr>
              <tr>
                <td colSpan={10} className="text-left">
                  {t(
                    '스타일과 스타일-컬러별 세일이 모두 대상일경우 스타일-컬러별 세일이 우선 적용됩니다.',
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <DetailHeader
        title={t(
          '적용대상 스타일 리스트(적용기간 : {{start}} ~ {{end}})',
          period,
        )}
        detailId={STYLE_DETAIL_ID}
        t={t}
      />
      <div
        id={STYLE_DETAIL_ID}
        className="table-area"
        data-state="closed"
        style={{ display: 'none' }}
      >
        <table className="table table-bordered-horizontal mb-30 text-center sales-table">
          <ItemHead withColor={false} t={t} />
          <tbody>
            {styles.map((item, index) => (
              <ItemRow key={index} item={item} withColor={false} />
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={10} className="text-right">
                {t('총 {{total}} 스타일', {
                  total: formatSerpText(source.PROD_INFO?.PROD_CNT),
                })}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
};

/** attributes.content — 레거시 조회 화면은 이 HTML 만 그대로 출력 */
export const buildSerpSellingPriceContentHtml = (
  source: SerpSellingPriceSource,
  t: TFunction,
): string =>
  renderToStaticMarkup(
    <div className="form-group">
      <SerpSellingPriceTables source={source} t={t} />
    </div>,
  );

/**
 * 저장 content(레거시 문서 포함) 그대로 — 「상세 보기」 버튼만 포탈 버튼으로 바꿔 접고 펴기.
 * 작성 화면도 저장할 HTML 을 이걸로 그림
 */
export const SerpSellingPriceSnapshot = ({ content }: { content: string }) => {
  const { t } = useTranslation();
  const [openIds, setOpenIds] = useState<Record<string, boolean>>({});

  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props, children }) => {
      if (tagName === 'button') {
        const detailId = element.getAttribute('data-value');
        if (!detailId || !DETAIL_IDS.has(detailId)) return undefined;
        const open = !!openIds[detailId];
        return (
          <BoxButton
            key={props.key as string}
            variant="secondary"
            size="small"
            aria-expanded={open}
            aria-controls={detailId}
            onClick={() =>
              setOpenIds((prev) => ({ ...prev, [detailId]: !prev[detailId] }))
            }
          >
            {open ? t('상세 닫기') : t('상세 보기')}
          </BoxButton>
        );
      }
      if (tagName === 'div' && DETAIL_IDS.has(element.id)) {
        const style = (props.style as Record<string, string>) ?? {};
        return createElement(
          'div',
          {
            ...props,
            'data-state': openIds[element.id] ? 'open' : 'closed',
            style: {
              ...style,
              display: openIds[element.id] ? 'block' : 'none',
            },
          },
          ...children,
        );
      }
      return undefined;
    },
    [openIds, t],
  );

  return (
    <SerpSellingPriceRoot>
      <SerpContentSnapshot content={content} transform={transform} />
    </SerpSellingPriceRoot>
  );
};
